<template>
  <div class="space-y-3 pb-8">
    <!-- Page Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
      <div>
        <h2 class="text-base font-bold text-gray-800">Courses</h2>
        <p class="text-xs text-gray-500 mt-0.5">Manage your institution's course catalog</p>
      </div>
      <Button
        label="New Course"
        icon="pi pi-plus"
        size="small"
        data-testid="btn-toggle-create-course-form"
        @click="showCreateForm = true"
      />
    </div>

    <!-- Create Form Drawer (overlay) -->
    <SlideOverDrawer v-model:visible="showCreateForm" header="Add New Courses" width="640px">
      <div class="p-1 space-y-5">
        <!-- Hint -->
        <div class="flex items-start gap-2 bg-primary/10 border border-primary/20 px-3 py-2.5 rounded-lg">
          <i class="pi pi-info-circle text-primary mt-0.5 shrink-0 text-sm"></i>
          <span class="text-xs text-primary-700">
            Fill in course details and click <strong>Add</strong> to queue. You can add multiple courses then submit them all at once.
          </span>
        </div>

        <!-- Form Row -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Course Title *</label>
            <InputText v-model="course_title" class="w-full" placeholder="e.g. Introduction to Computing" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Course Code *</label>
            <InputText v-model="course_code" class="w-full" placeholder="e.g. CSC 101" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Unit *</label>
            <InputNumber v-model="unit" :min="0" class="w-full" placeholder="e.g. 3" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Instance Year</label>
            <Select
              v-model="course_instance_year"
              :options="yearOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select year"
              class="w-full"
              showClear
            />
          </div>
        </div>
        <div class="flex justify-end">
          <Button size="small"
            label="Add"
            icon="pi pi-plus"
            :disabled="!course_title.trim() || !course_code.trim() || unit === null || unit === undefined"
            @click="addCourse"
          />
        </div>

        <!-- Queued Courses Table -->
        <div v-if="courses.length">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">
              Queued
              <span class="inline-flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full w-5 h-5 ml-1">
                {{ courses.length }}
              </span>
            </span>
            <Button
              v-if="courses.length > 1"
              label="Clear All"
              class="p-button-danger p-button-text p-button-sm"
              @click="courses = []"
            />
          </div>

          <DataTable :value="courses" size="small" stripedRows>
            <Column header="#" style="width: 3rem">
              <template #body="{ index }">
                <span class="text-gray-400 font-mono text-xs">{{ index + 1 }}</span>
              </template>
            </Column>
            <Column field="course_title" header="Title" />
            <Column field="course_code" header="Code" />
            <Column field="unit" header="Unit" style="width: 5rem" />
            <Column header="Year" style="width: 6rem">
              <template #body="{ data }">
                {{ data.course_instance_year || '—' }}
              </template>
            </Column>
            <Column style="width: 4rem">
              <template #body="{ index }">
                <Button icon="pi pi-trash" class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  @click="removeCourse(index)" v-tooltip.top="'Remove'" />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm text-gray-500">
            <span class="font-semibold text-gray-700">{{ courses.length }}</span> queued
          </span>
          <Button size="small"
            label="Submit All Courses"
            icon="pi pi-check"
            :loading="isSubmitting"
            :disabled="courses.length === 0"
            @click="submitAll"
          />
        </div>
      </template>
    </SlideOverDrawer>

    <!-- Filters -->
    <BillingFilterBar
      :search="filters.query"
      :debounce-ms="0"
      search-placeholder="Search courses..."
      :chips="filterChips"
      @update:search="filters.query = $event"
      @remove-chip="removeFilterChip"
      @clear="clearAllFilters"
    >
      <template #filters>
        <InputText v-model="filters.course_code" placeholder="Code" size="small" class="w-36" />
        <InputText v-model="filters.course_title" placeholder="Title" size="small" class="w-64" />
        <InputNumber :modelValue="filters.unit" placeholder="Unit" size="small" inputClass="w-20" :useGrouping="false" @input="onUnitInput" />
      </template>
    </BillingFilterBar>

    <!-- Course List Card -->
    <div class="bg-white border border-gray-300 rounded-xl shadow-sm overflow-hidden">
      <!-- Card Header -->
      <div class="px-5 py-4 border-b border-gray-300">
        <h3 class="font-semibold text-gray-700">
          All Courses
          <span v-if="totalRecords" class="text-sm font-normal text-gray-400 ml-1">({{ totalRecords }})</span>
        </h3>
      </div>

      <!-- Table Body -->
      <div class="p-0">
        <template v-if="loadingSchoolCourses">
          <div class="space-y-1.5 p-3">
            <div v-for="i in 6" :key="i" class="flex gap-3 items-center py-2 border-b border-gray-50">
              <Skeleton width="8%" height="0.875rem" />
              <Skeleton width="35%" height="0.875rem" />
              <Skeleton width="12%" height="0.875rem" />
              <Skeleton width="5%" height="0.875rem" />
              <Skeleton width="8rem" height="1.75rem" class="ml-auto" />
            </div>
          </div>
        </template>
        <template v-else>
          <!-- Bulk Action Bar -->
          <div v-if="selectedCourseRows.length > 0" class="flex flex-wrap items-center gap-2 px-3 py-2 mb-2 bg-primary/10 border border-primary/20 rounded-lg">
            <span class="text-xs font-semibold text-primary-700 mr-2">{{ selectedCourseRows.length }} selected</span>
            <div class="flex items-center gap-1">
              <InputNumber v-model="bulkUnit" :min="0" :max="20" placeholder="Unit" size="small" inputClass="w-14 text-center" :useGrouping="false" />
              <Button label="Set Unit" size="small" outlined class="whitespace-nowrap"
                :loading="bulkUpdatingCourses" :disabled="bulkUnit === null || bulkUnit === undefined" @click="bulkUpdateCourses('unit', bulkUnit)" />
            </div>
            <div class="flex items-center gap-1">
              <Select v-model="bulkInstanceYear" :options="yearOptions" optionLabel="label" optionValue="value" placeholder="Year" size="small" class="w-28" />
              <Button label="Set Year" size="small" outlined class="whitespace-nowrap"
                :loading="bulkUpdatingCourses" :disabled="!bulkInstanceYear" @click="bulkUpdateCourses('course_instance_year', bulkInstanceYear)" />
            </div>
            <Button label="Add to Curriculum" icon="pi pi-plus-circle" size="small"
              @click="showBulkCurriculumDialog = true" />
            <Button label="Deactivate" icon="pi pi-ban" size="small" outlined severity="warn"
              :loading="bulkUpdatingCourses" @click="bulkUpdateCourses('is_active', false)"
              v-tooltip.top="'Retire from course registration'" />
            <Button label="Delete" icon="pi pi-trash" size="small" outlined severity="danger"
              @click="openBulkDelete" />
            <Button icon="pi pi-times" text rounded size="small" severity="secondary"
              @click="selectedCourseRows = []" v-tooltip.top="'Clear'" />
          </div>

          <ReusablePrimeTable
            :headers="courseHeaders"
            :data="courseData"
            emptyMessage="No courses found"
            tableStyle="min-width: 500px;"
            :showCheckbox="true"
            v-model:selection="selectedCourseRows"
            :paginator="true"
            :lazy="true"
            :rows="rowsPerPage"
            :totalRecords="totalRecords"
            :first="firstRecord"
            :rowsPerPageOptions="[10, 25, 50, 100]"
            @page="onPageChange"
          >
            <template #actions="{ row }">
              <div class="flex gap-1 items-center">
                <Button icon="pi pi-eye" class="p-button-text p-button-rounded p-button-sm"
                  @click="handleView(row)" v-tooltip.top="'View'" />
                <Button icon="pi pi-pencil" class="p-button-text p-button-rounded p-button-sm"
                  @click="handleEdit(row)" v-tooltip.top="'Edit'" />
                <Button icon="pi pi-trash" class="p-button-danger p-button-text p-button-rounded p-button-sm"
                  @click="handleDelete(row)" v-tooltip.top="'Delete'" />
              </div>
            </template>
          </ReusablePrimeTable>
        </template>
      </div>
    </div>

    <!-- Edit Dialog -->
    <Dialog v-model:visible="showEditDialog" header="Edit Course" modal :style="{ width: '520px' }">
      <div class="space-y-4 pt-2">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Course Title *</label>
          <InputText v-model="editCourseTitle" class="w-full" />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Course Code *</label>
            <InputText v-model="editCourseCode" class="w-full" />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-gray-600">Unit *</label>
            <InputNumber v-model="editUnit" :min="0" class="w-full" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Instance Year</label>
          <Select
            v-model="editCourseInstanceYear"
            :options="yearOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select year"
            class="w-full"
            showClear
          />
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <Button size="small" label="Cancel" severity="secondary" @click="showEditDialog = false" />
        <Button size="small" label="Save Changes" :disabled="!editCourseTitle.trim() || !editCourseCode.trim() || editUnit === null || editUnit === undefined" @click="confirmUpdate" />
      </div>
    </Dialog>

    <!-- Delete Dialog -->
    <Dialog v-model:visible="showDeleteDialog" header="Delete Course" modal :style="{ width: '420px' }">
      <div class="flex items-start gap-3 pt-2">
        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 shrink-0">
          <i class="pi pi-trash text-red-500"></i>
        </div>
        <div>
          <p class="font-medium text-gray-800">Are you sure?</p>
          <p class="text-sm text-gray-500 mt-1">
            This will permanently delete
            <strong v-if="courseToDelete">"{{ courseToDelete.course_title }}"</strong>.
            This action cannot be undone.
          </p>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <Button size="small" label="Cancel" severity="secondary" @click="showDeleteDialog = false" />
        <Button size="small" label="Delete" severity="danger" icon="pi pi-trash" @click="confirmDelete" />
      </div>
    </Dialog>

    <!-- Bulk Delete Dialog -->
    <Dialog v-model:visible="showBulkDeleteDialog" header="Delete Courses" modal :style="{ width: '480px' }">
      <template v-if="bulkDeleteOutcomes.length === 0">
        <div class="flex items-start gap-3 pt-2">
          <div class="flex items-center justify-center w-10 h-10 rounded-full bg-red-50 shrink-0">
            <i class="pi pi-trash text-red-500"></i>
          </div>
          <div>
            <p class="font-medium text-gray-800">Delete {{ selectedCourseRows.length }} selected course(s)?</p>
            <p class="text-sm text-gray-500 mt-1">
              Courses that already carry registrations, allocations or other academic
              records are skipped and reported back — nothing historical is touched.
              This action cannot be undone.
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-2 mt-6">
          <Button size="small" label="Cancel" severity="secondary" @click="showBulkDeleteDialog = false" />
          <Button size="small" label="Delete" severity="danger" icon="pi pi-trash"
            :loading="bulkDeleting" @click="confirmBulkDelete" />
        </div>
      </template>
      <template v-else>
        <p class="text-sm text-gray-600 pt-2">
          {{ bulkDeleteSummary.deletedCount }} deleted,
          {{ bulkDeleteSummary.blocked.length }} kept.
        </p>
        <ul class="mt-3 space-y-2 max-h-60 overflow-y-auto">
          <li v-for="o in bulkDeleteSummary.blocked" :key="o.id"
            class="flex items-start gap-2 text-sm">
            <i class="pi pi-lock text-amber-500 mt-0.5 text-xs"></i>
            <span>
              <span class="font-medium text-gray-800">{{ blockedCourseLabel(o, bulkDeleteRows) }}</span>
              <span class="text-gray-500"> — {{ o.reason }}</span>
            </span>
          </li>
        </ul>
        <div class="flex justify-end gap-2 mt-6">
          <Button size="small" label="Close" severity="secondary" @click="showBulkDeleteDialog = false" />
          <Button v-if="bulkDeleteSummary.deactivatableIds.length" size="small"
            :label="`Deactivate ${bulkDeleteSummary.deactivatableIds.length} kept course(s)`"
            severity="warn" icon="pi pi-ban" :loading="bulkDeleting" @click="deactivateBlockedCourses" />
        </div>
      </template>
    </Dialog>

    <BulkAddToCurriculumDialog v-model:visible="showBulkCurriculumDialog"
      :courses="selectedForCurriculum" @success="onCurriculumSuccess" />
  </div>
</template>

<script setup lang="ts">
import BulkAddToCurriculumDialog from '../BulkAddToCurriculumDialog.vue';
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useCourses } from '@/composables/queries/useCourses';
import ReusablePrimeTable from '@/components/common/ReusablePrimeTable.vue';
import SlideOverDrawer from '@/components/common/SlideOverDrawer.vue';
import { useToast } from 'primevue/usetoast';
import { debounce } from '@/utils';
import { getErrorMessage } from '@/utils/error';
import { summarizeDeleteOutcomes, type CourseDeleteOutcome } from '@/utils/courseDeleteOutcomes';
import { blockedCourseLabel, type SelectedCourseRow } from '@/utils/courseDeleteLabel';
import { courseInstanceYear, formatCourseInstance } from '@/utils/courseInstance';

import { extractGraphQLError } from '@/utils/graphqlError';
const toast = useToast();
const showSubmitCoursesDialog = ref(false);

const showCreateForm = ref(false);

const filters = ref({
  query: '',
  course_title: '',
  course_code: '',
  unit: undefined as number | undefined,
});

const filterChips = computed(() => {
  const chips: { key: string; label: string }[] = [];
  if (filters.value.course_code) chips.push({ key: 'course_code', label: `Code: ${filters.value.course_code}` });
  if (filters.value.course_title) chips.push({ key: 'course_title', label: `Title: ${filters.value.course_title}` });
  if (filters.value.unit != null) chips.push({ key: 'unit', label: `Unit: ${filters.value.unit}` });
  return chips;
});

function removeFilterChip(key: string) {
  if (key === 'course_code') filters.value.course_code = '';
  else if (key === 'course_title') filters.value.course_title = '';
  else if (key === 'unit') filters.value.unit = undefined;
}

function clearAllFilters() {
  filters.value = { query: '', course_title: '', course_code: '', unit: undefined };
  clearFilter();
}

const { createTertiaryCourse, updateTertiaryCourse, deleteTertiaryCourse } = useTertiaryMutations();

const {
  courses: courseTable,
  paginatorInfo,
  loadingSchoolCourses,
  refetchSchoolCourses,
  setPage,
  setItemsPerPage,
  setFilter,
  clearFilter,
} = useCourses({ autoFetch: true });

const course_title = ref('');
const course_code = ref('');
const unit = ref<number | null>(null);
const course_instance_year = ref<string | null>(null);

type Course = {
  id: string;
  course_title: string;
  course_code: string;
  unit: number;
};

type CourseRow = {
  id: string;
  course_title: string;
  course_code: string;
  unit: number;
  course_instance_id?: string | null;
  course_instance_year?: number | string | null;
  actions?: string;
};

type DeleteCourse = {
  id: string;
  course_title: string;
  course_code: string;
  unit: number;
};

// Bulk update courses
const selectedCourseRows = ref<Record<string, unknown>[]>([]);
const showBulkCurriculumDialog = ref(false);
const selectedForCurriculum = computed(() =>
  selectedCourseRows.value.map(r => ({
    id: String(r.id), course_code: String(r.course_code),
    course_title: String(r.course_title), unit: Number(r.unit),
  })),
);
function onCurriculumSuccess() {
  selectedCourseRows.value = [];
}
const bulkUnit = ref<number | null>(null);
const bulkInstanceYear = ref<string | null>(null);
const bulkUpdatingCourses = ref(false);

const BULK_UPDATE_COURSES = gql`
  mutation BulkUpdateCourses($ids: [ID!]!, $unit: Int, $course_instance_year: String, $is_active: Boolean) {
    bulkUpdateCourses(ids: $ids, unit: $unit, course_instance_year: $course_instance_year, is_active: $is_active) {
      success message
    }
  }
`;
const { mutate: bulkUpdateCoursesMutate } = useMutation(BULK_UPDATE_COURSES);

const DELETE_COURSES = gql`
  mutation DeleteCourses($ids: [ID!]!) {
    deleteCourses(ids: $ids) {
      deleted_count
      blocked_count
      outcomes { id course_code deleted reason }
    }
  }
`;
const { mutate: deleteCoursesMutate } = useMutation(DELETE_COURSES);

const showBulkDeleteDialog = ref(false);
const bulkDeleting = ref(false);
const bulkDeleteOutcomes = ref<CourseDeleteOutcome[]>([]);
// Snapshot of the rows being deleted: the selection is cleared once the
// mutation returns, but the kept-course list still needs their names.
const bulkDeleteRows = ref<SelectedCourseRow[]>([]);
const bulkDeleteSummary = computed(() => summarizeDeleteOutcomes(bulkDeleteOutcomes.value));

function openBulkDelete() {
  bulkDeleteOutcomes.value = [];
  bulkDeleteRows.value = selectedCourseRows.value.map((r) => ({
    id: String(r.id), course_code: String(r.course_code ?? ''), course_title: String(r.course_title ?? ''),
  }));
  showBulkDeleteDialog.value = true;
}

async function confirmBulkDelete() {
  if (!selectedCourseRows.value.length) return;
  bulkDeleting.value = true;
  try {
    const ids = selectedCourseRows.value.map((r) => String(r.id));
    const result = await deleteCoursesMutate({ ids });
    const payload = result?.data?.deleteCourses;
    if (!payload) {
      toast.add({ severity: 'error', summary: 'Delete Failed', detail: 'Failed to delete courses.', life: 5000 });
      return;
    }
    bulkDeleteOutcomes.value = payload.outcomes ?? [];
    if (payload.deleted_count > 0) {
      toast.add({ severity: 'success', summary: 'Deleted', detail: `${payload.deleted_count} course(s) deleted.`, life: 3000 });
    }
    if (payload.blocked_count === 0) {
      showBulkDeleteDialog.value = false;
    }
    selectedCourseRows.value = [];
    refetchSchoolCourses();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  } finally {
    bulkDeleting.value = false;
  }
}

async function deactivateBlockedCourses() {
  const ids = bulkDeleteSummary.value.deactivatableIds;
  if (!ids.length) return;
  bulkDeleting.value = true;
  try {
    const result = await bulkUpdateCoursesMutate({ ids, is_active: false });
    const msg = result?.data?.bulkUpdateCourses;
    if (!msg?.success) {
      toast.add({ severity: 'error', summary: 'Update Failed', detail: msg?.message || 'Failed to deactivate courses.', life: 5000 });
      return;
    }
    toast.add({ severity: 'success', summary: 'Deactivated', detail: `${ids.length} course(s) retired from course registration.`, life: 3000 });
    showBulkDeleteDialog.value = false;
    refetchSchoolCourses();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  } finally {
    bulkDeleting.value = false;
  }
}

async function bulkUpdateCourses(field: string, value: string | number | boolean | null) {
  if ((value === null || value === undefined) || !selectedCourseRows.value.length) return;
  bulkUpdatingCourses.value = true;
  try {
    const ids = selectedCourseRows.value.map((r) => String(r.id));
    const vars: Record<string, unknown> = { ids };
    vars[field] = field === 'unit' ? Number(value) : value;
    const result = await bulkUpdateCoursesMutate(vars);
    const msg = result?.data?.bulkUpdateCourses;
    if (!msg?.success) {
      toast.add({ severity: 'error', summary: 'Update Failed', detail: msg?.message || 'Failed to update courses.', life: 5000 });
      return;
    }
    toast.add({ severity: 'success', summary: 'Updated', detail: msg.message, life: 3000 });
    selectedCourseRows.value = [];
    bulkUnit.value = null;
    bulkInstanceYear.value = '';
    refetchSchoolCourses();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: getErrorMessage(e), life: 5000 });
  } finally {
    bulkUpdatingCourses.value = false;
  }
}

const showEditDialog = ref(false);
const showDeleteDialog = ref(false);
const courseToEdit = ref<Course>();
const courseToDelete = ref<DeleteCourse>();

const editCourseTitle = ref('');
const editCourseCode = ref('');
const editCourseInstanceYear = ref<string | null>(null);
const editUnit = ref<number | null>(null);

const courses = ref<{ course_title: string; course_code: string; unit: number; course_instance_year?: string; }[]>([]);
const isSubmitting = ref(false);

function addCourse() {
  if (course_title.value.trim() && course_code.value.trim() && unit.value !== null && unit.value !== undefined) {
    courses.value.push({
      course_title: course_title.value.trim(),
      course_code: course_code.value.trim(),
      unit: unit.value,
      ...(course_instance_year.value
        ? { course_instance_year: course_instance_year.value }
        : {}),
    });
    course_title.value = '';
    course_code.value = '';
    unit.value = null;
    course_instance_year.value = null;
  }
}

function removeCourse(idx: number) {
  courses.value.splice(idx, 1);
}

async function submitAll() {
  isSubmitting.value = true;
  const total = courses.value.length;
  let failed = 0;
  for (const course of courses.value) {
    try {
      await createTertiaryCourse(course);
    } catch (err) {
      failed++;
      toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, `Failed to create ${course.course_title}`), life: 3000 });
    }
  }
  await refetchSchoolCourses();
  isSubmitting.value = false;
  if (failed < total) {
    toast.add({ severity: 'success', summary: 'Success', detail: `${total - failed} course(s) created successfully`, life: 3000 });
  }
  courses.value = [];
}

const courseHeaders = [
  { field: 'course_code', header: 'Code' },
  { field: 'course_title', header: 'Title' },
  { field: 'course_instance', header: 'Course Instance' },
  { field: 'unit', header: 'Unit' },
  { field: 'actions', header: 'Actions' },
];

const courseData = computed(() =>
  (courseTable.value || []).map((course) => ({ ...course, course_instance: formatCourseInstance(course) })),
);

const rowsPerPage = computed(() => Number(paginatorInfo?.value?.perPage) || 50);
const totalRecords = computed(() => Number(paginatorInfo?.value?.total) || 0);
const firstRecord = computed(() => (((Number(paginatorInfo?.value?.currentPage) || 1) - 1) * rowsPerPage.value) || 0);

async function onPageChange(event: { page?: number; first?: number; rows?: number }) {
  try {
    if (typeof event.rows === 'number' && event.rows !== rowsPerPage.value) {
      await setItemsPerPage(event.rows);
      return;
    }

    if (typeof event.page === 'number') {
      await setPage(event.page + 1);
      return;
    }

    if (typeof event.first === 'number' && typeof event.rows === 'number') {
      const newPage = Math.floor(event.first / event.rows) + 1;
      await setPage(newPage);
    }
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unable to change page', life: 3000 });
  }
}

async function confirmUpdate() {
  if (!courseToEdit.value) return;
  try {
    await updateTertiaryCourse(courseToEdit.value.id, {
      course_title: editCourseTitle.value,
      course_code: editCourseCode.value,
      course_instance_year: editCourseInstanceYear.value,
      unit: editUnit.value || 0,
    });
    await refetchSchoolCourses();
    toast.add({ severity: 'success', summary: 'Updated', detail: 'Course updated successfully', life: 3000 });
    showEditDialog.value = false;
    courseToEdit.value = undefined;
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, 'Error updating course'), life: 3000 });
  }
}

async function confirmDelete() {
  if (!courseToDelete.value) return;
  try {
    await deleteTertiaryCourse(courseToDelete.value.id);
    await refetchSchoolCourses();
    toast.add({ severity: 'success', summary: 'Deleted', detail: 'Course deleted successfully', life: 3000 });
    showDeleteDialog.value = false;
    courseToDelete.value = undefined;
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Error', detail: extractGraphQLError(err, 'Error deleting course'), life: 3000 });
  }
}

function handleEdit(row: CourseRow) {
  courseToEdit.value = row;
  editCourseTitle.value = row.course_title;
  editCourseCode.value = row.course_code;
  const instanceYear = courseInstanceYear(row);
  editCourseInstanceYear.value = instanceYear === null ? '' : String(instanceYear);
  editUnit.value = row.unit;
  showEditDialog.value = true;
}

function handleDelete(row: DeleteCourse) {
  courseToDelete.value = row;
  showDeleteDialog.value = true;
}

// InputNumber commits its v-model only on blur/Enter; use @input to filter live as the user types.
function onUnitInput(event: { value: unknown }) {
  const raw = event.value;
  filters.value.unit = raw === null || raw === undefined || raw === '' ? undefined : Number(raw);
}

async function applyFilter() {
  await setPage(1);
  await setFilter({
    query: filters.value.query || undefined,
    course_title: filters.value.course_title || undefined,
    course_code: filters.value.course_code || undefined,
    unit: filters.value.unit || undefined,
  });
}

const applyFilterDebounced = debounce(applyFilter, 300);

// Watch for query changes (debounced)
watch(
  () => filters.value.query,
  () => {
    applyFilterDebounced();
  },
);

// Watch for course_title changes (debounced)
watch(
  () => filters.value.course_title,
  () => {
    applyFilterDebounced();
  },
);

// Watch for course_code changes (debounced)
watch(
  () => filters.value.course_code,
  () => {
    applyFilterDebounced();
  },
);

// Watch for unit changes (immediate)
watch(
  () => filters.value.unit,
  async() => {
    await applyFilter();
  },
);

const currentYear = new Date().getFullYear();

const yearOptions = computed(() => {
  const startYear = 2022;
  const endYear = currentYear + 5;
  return Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => {
      const year = startYear + i;
      return { label: `${year}`, value: `${year}` };
    },
  );
});

const router = useRouter();

// ...existing code...
function handleView(row: CourseRow) {
  router.push({ name: 'course-details', params: { id: row.id } });
}
// ...existing code...

watch(
  filters,
  async(newFilters) => {
    const isEmpty =
      !newFilters.query &&
      !newFilters.course_title &&
      !newFilters.course_code &&
      newFilters.unit == null;
    if (isEmpty) {
      await clearFilter();
    }
  },
  { deep: true },
);
</script>
